import { Component, ElementRef, effect, inject, signal, viewChild } from '@angular/core';

import { SessionRevokedService } from '@core/services/session-revoked.service';

/**
 * Blocking notice shown once when the API reports the session as ended (401 session_revoked).
 * No close button, no Escape, no backdrop dismiss: "Abmelden" is the only action.
 */
@Component({
  selector: 'app-session-revoked-dialog',
  template: `
    @if (sessionRevoked.visible()) {
      <div class="session-revoked-overlay">
        <div
          class="session-revoked-card card card-elevated"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="session-revoked-title"
          aria-describedby="session-revoked-message"
        >
          <h3 id="session-revoked-title">Sitzung beendet</h3>
          <p id="session-revoked-message">
            Ihre Sitzung wurde beendet, zum Beispiel auf einem anderen Gerät. Sie werden jetzt abgemeldet und
            können sich danach neu anmelden.
          </p>
          <button
            #logoutButton
            type="button"
            class="btn btn-primary"
            [disabled]="loggingOut()"
            [attr.aria-busy]="loggingOut() || null"
            (click)="logout()"
          >
            {{ loggingOut() ? 'Wird abgemeldet…' : 'Abmelden' }}
          </button>
        </div>
      </div>
    }
  `,
  styles: `
    .session-revoked-overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 21, 32, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--s-6);
      z-index: 2000;
    }

    .session-revoked-card {
      width: 100%;
      max-width: 420px;
      display: flex;
      flex-direction: column;
      gap: var(--s-4);

      h3 {
        font-weight: var(--font-weight-heading);
      }

      p {
        color: var(--color-text-secondary);
      }

      .btn {
        align-self: flex-end;
      }
    }
  `,
})
export class SessionRevokedDialog {
  protected readonly sessionRevoked = inject(SessionRevokedService);
  protected readonly loggingOut = signal(false);

  private readonly logoutButton = viewChild<ElementRef<HTMLButtonElement>>('logoutButton');

  constructor() {
    // The only action of the notice gets the focus, so Enter confirms and Tab cannot wander behind it.
    effect(() => this.logoutButton()?.nativeElement.focus());
  }

  protected logout(): void {
    // A double click would fire the logout redirect twice; the page is gone in a moment anyway.
    this.loggingOut.set(true);
    this.sessionRevoked.confirm();
  }
}
